import { format, parseISO, addHours } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Converts a date to Argentina timezone (UTC-3)
 * @param date Date to convert
 * @returns Date adjusted to Argentina timezone
 */
export const toArgentinaDate = (date: Date | string): Date => {
  const inputDate = typeof date === 'string' ? new Date(date) : date;
  
  // Create a date string in Argentina timezone
  const argentinaDateString = inputDate.toLocaleString('en-US', {
    timeZone: 'America/Argentina/Buenos_Aires'
  });
  
  // Parse the date string back to a Date object
  return new Date(argentinaDateString);
};

/**
 * Gets the current date in Argentina timezone (UTC-3)
 * @returns Current date in Argentina timezone
 */
export const getCurrentArgentinaDate = (): Date => {
  return toArgentinaDate(new Date());
};

/**
 * Parsea un string YYYY-MM-DD como fecha en Buenos Aires (evita off-by-one por UTC)
 */
const parseDateOnlyAsBuenosAires = (dateStr: string): Date => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return new Date(dateStr + 'T12:00:00-03:00');
  }
  return parseISO(dateStr);
};

/**
 * Formats a date to Argentina timezone (Buenos Aires) with the specified format
 * @param date Date to format (YYYY-MM-DD strings are interpreted as Buenos Aires calendar date)
 * @param formatStr Format string (date-fns format)
 * @returns Formatted date string in Argentina timezone
 */
export const formatArgentinaDate = (
  date: Date | string | null | undefined,
  formatStr: string = 'dd/MM/yyyy'
): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? parseDateOnlyAsBuenosAires(date) : date;
  const argentinaDate = toArgentinaDate(d);
  return format(argentinaDate, formatStr, { locale: es });
};

/**
 * Converts a date to ISO string in Argentina timezone (UTC-3)
 * @param date Date to convert
 * @returns ISO string in Argentina timezone (YYYY-MM-DD)
 */
export const toArgentinaISOString = (date: Date | string): string => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Argentina/Buenos_Aires',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const d = typeof date === 'string' ? new Date(date) : date;
  return formatter.format(d); // en-CA yields YYYY-MM-DD
};

/**
 * Gets the current date in Argentina timezone (Buenos Aires) as an ISO string
 * @returns Current date in Buenos Aires as YYYY-MM-DD
 */
export const getCurrentArgentinaISOString = (): string => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Argentina/Buenos_Aires',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(new Date());
};

/**
 * Adjusts a date input for form fields to Argentina timezone (UTC-3)
 * @param date Date to adjust
 * @returns Date string in YYYY-MM-DD format for form inputs
 */
export const adjustDateForInput = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  const argentinaDate = toArgentinaDate(typeof date === 'string' ? parseISO(date) : date);
  return format(argentinaDate, 'yyyy-MM-dd');
};

/**
 * Converts a date to a database-friendly ISO string with timezone adjustment
 * @param date Date to convert
 * @returns ISO string with timezone adjustment for database storage
 */
export const toDBFriendlyDate = (date: Date | string): string => {
  const inputDate = typeof date === 'string' ? new Date(date) : date;
  
  // Add 3 hours to compensate for UTC-3 timezone when storing in database
  // This ensures the date is stored correctly in UTC but represents Argentina's date
  const adjustedDate = addHours(inputDate, 3);
  return adjustedDate.toISOString().split('T')[0];
};